import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  viewChild,
} from '@angular/core';
import { Modal } from 'flowbite';
import { IBookingScheduler, ICustomToggleOptions, IFavourites } from 'src/app/models';
import { CustomToggleTone, FavouriteRouteType, FavouriteType } from 'src/app/models/enums';
import { LiteralService, UtilsService } from 'src/app/services';
import {
  IBookingsSchedulersState,
  IFavouritesState,
} from 'src/app/store/states';
import { images } from 'src/images';

@Component({
  selector: 'app-modal-favourite-routes',
  templateUrl: './modal-favourite-routes.component.html',
  styleUrl: './modal-favourite-routes.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalFavouriteRoutesComponent {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() text: string;
  @Input() favourites: IFavouritesState;
  @Input() recurrents: IBookingsSchedulersState;
  @Input() isFromUser: boolean;
  @Output() book = new EventEmitter<IFavourites>();
  @Output() edit = new EventEmitter();
  @Output() delete = new EventEmitter();
  @Output() goToNewFavouriteEmitter = new EventEmitter();
  @Output() changePaginationEmitter = new EventEmitter();
  @Output() deleteRecurrent = new EventEmitter();
  @Output() editRecurrent = new EventEmitter();

  public selectedFavourite: IFavourites | undefined;
  public selectedFavouriteType = FavouriteType.PUNTUAL;
  public selectedRecurrent: IBookingScheduler | undefined;

  public FavouriteType = FavouriteType;
  public FavouriteRouteType = FavouriteRouteType;

  public images = images;
  readonly favouriteTypeToggleOptions: ICustomToggleOptions<FavouriteType> = [
    {
      value: FavouriteType.PUNTUAL,
      image: images.favourites.calendarFavourite,
      descriptionKey: 'favourites.puntual',
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: FavouriteType.RECURRENT,
      image: images.favourites.recurrent,
      descriptionKey: 'favourites.recurrent',
      tone: CustomToggleTone.SECONDARY,
    },
  ];

  @ViewChild('form') form: ElementRef;
  readonly modalContainer = viewChild.required<ElementRef<HTMLElement>>('modalContainer');

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['favourites'] &&
      changes['favourites'].currentValue.value.length === 0
    )
      this.selectedFavourite = undefined;
    if (
      changes['recurrents'] &&
      changes['recurrents'].currentValue.value.length === 0
    )
      this.selectedRecurrent = undefined;
    if (changes['favourites'] && changes['recurrents']) {
      if (
        changes['favourites'].currentValue.value.length === 0 &&
        Object.keys(changes['favourites'].currentValue.pagination).length ===
          0 &&
        changes['recurrents'].currentValue.value.length === 0 &&
        Object.keys(changes['recurrents'].currentValue.pagination).length === 0
      ) {
        this.selectedFavouriteType = FavouriteType.PUNTUAL;
      }
    }
  }

  selectFavouriteRoute(favourite: IFavourites) {
    if (this.selectedFavourite?.id === favourite.id) {
      this.selectedFavourite = undefined;
    } else {
      this.selectedFavourite = favourite;
    }
  }

  selectRecurrent(recurrent: IBookingScheduler) {
    if (this.selectedRecurrent?.id === recurrent.id) {
      this.selectedRecurrent = undefined;
    } else {
      this.selectedRecurrent = recurrent;
    }
  }

  onDelete() {
    this.delete.emit(this.selectedFavourite!.id);
  }

  newBooking() {
    this.book.emit(this.selectedFavourite);
  }

  onNewFavourite() {
    this.goToNewFavouriteEmitter.emit();
  }

  onEdit() {
    this.edit.emit(this.selectedFavourite);
  }

  onDeleteRecurrent() {
    this.deleteRecurrent.emit(this.selectedRecurrent!.id);
  }

  onEditRecurrent() {
    this.editRecurrent.emit(this.selectedRecurrent);
  }

  onChangePagination(event: any) {
    this.selectedFavourite = undefined;
    this.changePaginationEmitter.emit(event);
  }

  onModalClick(event: any) {
    const formElement = this.form.nativeElement as HTMLElement;
    if (!formElement?.contains(event.target)) {
      this.selectedFavourite = undefined;
    }
    event.stopPropagation();
  }

  closeModal(): void {
    this.selectedFavourite = undefined;
    this.selectedRecurrent = undefined;
    this.modal.hide();
  }

  passengers(favourite: IFavourites): number {
    return favourite.favoriteSeats!.length > 0
      ? favourite.favoriteSeats!.filter((seat) => !seat.prm).length
      : favourite.passengers!;
  }

  prmPassengers(favourite: IFavourites): number {
    return favourite.favoriteSeats!.length > 0
      ? favourite.favoriteSeats!.filter((seat) => seat.prm).length
      : favourite.prmPassengers!;
  }

  changeSelectedFavouriteType() {
    if (this.selectedFavouriteType === FavouriteType.PUNTUAL) {
      this.selectedFavouriteType = FavouriteType.RECURRENT;
    } else {
      this.selectedFavouriteType = FavouriteType.PUNTUAL;
    }
  }

  getDays(weekDays: any[]) {
    const sortedWeekDays = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    weekDays.sort(
      (a, b) =>
        sortedWeekDays.indexOf(a.toLowerCase()) -
        sortedWeekDays.indexOf(b.toLowerCase()),
    );
    let days = '';
    for (let index = 0; index < weekDays.length; index++) {
      const element = this.literalService.get(
        'calendar.longWeekDays.' +
          weekDays[index][0].toLowerCase() +
          weekDays[index][1].toLowerCase() +
          weekDays[index][2].toLowerCase(),
        true,
      );
      days += element;
      if (index < weekDays.length - 1) days += ', ';
    }
    return days;
  }
}
